import dayjs from 'dayjs';
import { useEffect } from 'react';

/**
 * Буцаалтын захиалга үүсгэх урсгалын хагас дутуу мэдээллийг (draft) хадгалах.
 *
 * Бүх төлөв нь React context-д байдаг тул хуудсаа refresh хийхэд сонгосон
 * захиалга, буцаах/солих бараа, хаяг алга болдог байсан. sessionStorage-д
 * хадгалснаар refresh хийсэн ч сэргэнэ.
 *
 * Аль алхам дээр байгааг нь URL-ийн `?step=` параметр агуулна (Add.js) —
 * ингэснээр refresh, browser-ийн back/forward хоёулаа ажиллана.
 *
 * ХАМРАХ ХҮРЭЭ: draft нь ЗӨВХӨН тухайн хуудас дээр байх хугацаанд л хүчинтэй.
 * Өөр хуудас руу орвол `useClearDraftOnLeave` шууд цэвэрлэдэг.
 *
 * Хүргэлт (views/orderDelivery/Add/draft.js), татан авалт (views/orders/draft.js)
 * дээр ижил механизм байгаа — зан төлөвийг өөрчлөх бол бүгдийг харна.
 */
export const RETURN_DRAFT_KEY = 'add_order_return_draft';

/**
 * Draft-ын key-г merchant тус бүрд салгана.
 *
 * `changeMerchant` (store/createUserSlice.js) нь merchant солиход
 * `window.location.href`-ээр НЭГ ЛЭ таб дотор дахин уншуулдаг. Key нь тогтмол
 * байсан бол өмнөх merchant дээр сонгосон захиалга, бараа шинэ merchant дээр
 * сэргэх байсан.
 */
export const returnDraftKey = (merchantCode) =>
  merchantCode ? `${RETURN_DRAFT_KEY}_${merchantCode}` : RETURN_DRAFT_KEY;

/**
 * Солих барааны (агуулах/салбар) төлөв нь тусдаа provider-т байдаг тул
 * өөрийн key-тэй — хоёр provider бие биенийхээ бичлэгийг дарж бичихгүй.
 */
export const exchangeDraftKey = (key) => (key ? `${key}_exchange` : key);

/** JSON болгоход dayjs объектууд мөр болдог тул сэргээж эргүүлнэ */
const reviveDate = (value) => (value ? dayjs(value) : value);

/** Огноо/цагийн талбарууд — DatePicker, TimePicker, RangePicker-ийн утгууд */
const DATE_KEYS = ['pickupDate', 'pickupTime', 'dropoffDate', 'receiveDate', 'receiveTime'];

/** Формын огноо/цагийн талбаруудыг dayjs болгож эргүүлнэ (массивыг ч дэмжинэ) */
const reviveFormValues = (values) => {
  if (!values || typeof values !== 'object') return values;

  const revived = { ...values };
  DATE_KEYS.forEach((key) => {
    if (!revived[key]) return;
    revived[key] = Array.isArray(revived[key])
      ? revived[key].map(reviveDate)
      : reviveDate(revived[key]);
  });
  // RangePicker — үргэлж массив
  if (Array.isArray(revived.dropoffTime)) revived.dropoffTime = revived.dropoffTime.map(reviveDate);

  return revived;
};

/**
 * `formDataStep2` нь дээд түвшиндээ ч, `formValues` дотроо ч огноо агуулдаг
 * (Step2 нь `{...values, formValues}` гэж хадгалдаг) — хоёуланг сэргээнэ.
 */
const reviveDraft = (draft) => {
  if (!draft?.formDataStep2 || Array.isArray(draft.formDataStep2)) return draft;

  const step2 = reviveFormValues(draft.formDataStep2);
  return {
    ...draft,
    formDataStep2: {
      ...step2,
      ...(step2.formValues ? { formValues: reviveFormValues(step2.formValues) } : {}),
    },
  };
};

export const readDraft = (key) => {
  if (!key) return {};

  try {
    const raw = sessionStorage.getItem(key);
    return raw ? reviveDraft(JSON.parse(raw)) : {};
  } catch (error) {
    console.error('readDraft: ', error);
    return {};
  }
};

export const writeDraft = (key, draft) => {
  if (!key) return;

  try {
    sessionStorage.setItem(key, JSON.stringify(draft));
  } catch (error) {
    // Хэмжээ хэтэрсэн зэрэг тохиолдолд урсгалыг тасалдуулахгүй
    console.error('writeDraft: ', error);
  }
};

/** Захиалга амжилттай үүссэний дараа (Step3) дуудаж draft-ыг цэвэрлэнэ */
export const clearDraft = (key) => {
  if (!key) return;

  try {
    sessionStorage.removeItem(key);
    sessionStorage.removeItem(exchangeDraftKey(key));
  } catch (error) {
    console.error('clearDraft: ', error);
  }
};

/**
 * Хуудсаас гармагц draft-ыг цэвэрлэнэ.
 *
 * Захиалга үүсгэх хуудас unmount болох (өөр хуудас руу орох, толгойн "back",
 * breadcrumb, хажуугийн меню, browser-ийн back — бүгд) үед л cleanup ажиллана.
 *
 * Refresh дээр ХАМААРАХГҮЙ: хуудас дахин уншихад JS бүхэлдээ унтардаг тул
 * React cleanup ажиллаж чаддаггүй — refresh-ийн сэргээлт хэвээрээ.
 *
 * Алхам солих (`?step=`) нь зөвхөн search-ийг өөрчилдөг, route нэг хэвээрээ
 * тул unmount болохгүй — алхам хооронд draft хадгалагдана.
 */
export const useClearDraftOnLeave = (key) => {
  useEffect(() => () => clearDraft(key), [key]);
};
